// src/pages/clubs/ClubWebsiteEditor.jsx
// Club-admin editor for the public club website (features/club-sites/).
// Route: /clubs/:clubId/website
//
// Edits a draft in clubSiteDrafts/{clubId} (club-staff RW per rules), then
// Publish calls the publishClubSite callable, which sanitizes + snapshots
// into the public clubSites/{clubId} doc. The public site itself is served
// by the standalone renderer (deferred / demand-gated — A-10/A-11); this
// page is the editor + an in-app preview, both of which work at $0.

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/firebase/firebase';
import { uploadBlobToStorage } from '@/services/storageService';
import useClub from '@/hooks/useClub';
import PageSpinner from '@/components/PageSpinner';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ChevronLeftIcon, GlobeAltIcon, PlusIcon, TrashIcon, SparklesIcon,
  EyeIcon, PencilSquareIcon, CheckCircleIcon, ArrowTopRightOnSquareIcon, PhotoIcon,
  ChevronUpIcon, ChevronDownIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const inputCls = 'w-full rounded-xl border border-zinc-300 bg-white px-3.5 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 transition';

// Page types the admin can add beyond the always-on Home page. Only About and
// Custom text render as their own section (renderer textPages = about|text);
// teams/schedule/sponsors/contact are data-driven sections of their own, so we
// don't offer them here as pages (they'd be silent no-ops). See ClubSite.jsx.
const ADDABLE_PAGES = [
  { type: 'about', label: 'About',            bilingualBody: true },
  { type: 'text',  label: 'Custom text page', bilingualBody: true },
];

// Social platforms (mirrors SOCIAL_KEYS in functions/modules/clubSites.js).
const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourclub' },
  { key: 'facebook',  label: 'Facebook',  placeholder: 'https://facebook.com/yourclub' },
  { key: 'x',         label: 'X',          placeholder: 'https://x.com/yourclub' },
  { key: 'youtube',   label: 'YouTube',    placeholder: 'https://youtube.com/@yourclub' },
  { key: 'tiktok',    label: 'TikTok',     placeholder: 'https://tiktok.com/@yourclub' },
  { key: 'website',   label: 'Website',    placeholder: 'https://yourclub.org' },
];

// Orderable / hideable content sections (mirrors SECTION_KEYS in
// functions/modules/clubSites.js). Order here is the default order.
const SECTION_LABELS = [
  ['affiliations', 'Affiliations'], ['about', 'About / pages'], ['teams', 'Teams'],
  ['seasons', 'Registration'], ['schedule', 'Schedule'], ['results', 'Recent results'], ['news', 'News'],
  ['camps', 'Camps & clinics'], ['gallery', 'Photos'], ['commits', 'College commitments'],
  ['staff', 'Coaching staff'], ['sponsors', 'Sponsors'],
];
const SECTION_LABEL = Object.fromEntries(SECTION_LABELS);
const SECTION_ORDER_DEFAULT = SECTION_LABELS.map(([k]) => k);

// Normalize draft.sections into the full ordered list: known keys in the
// club's order first, missing keys appended (visible), unknown dropped.
function resolveSections(sections) {
  const known = new Set(SECTION_ORDER_DEFAULT);
  const seen = new Set();
  const out = [];
  for (const s of (Array.isArray(sections) ? sections : [])) {
    const key = s && typeof s.key === 'string' ? s.key : null;
    if (!key || !known.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push({ key, visible: s.visible !== false });
  }
  for (const key of SECTION_ORDER_DEFAULT) if (!seen.has(key)) out.push({ key, visible: true });
  return out;
}
function moveSection(list, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// Affiliation preset (mirrors AFFILIATIONS in functions/modules/clubSites.js).
const AFFILIATION_OPTIONS = [
  ['ecnl', 'ECNL'], ['ecrl', 'ECNL RL'], ['mls_next', 'MLS NEXT'], ['ga', 'Girls Academy'], ['npl', 'NPL'],
  ['us_club_soccer', 'US Club Soccer'], ['usys', 'US Youth Soccer'], ['cal_south', 'Cal South'],
  ['socal', 'SOCAL'], ['csl', 'Coast Soccer League'], ['ayso', 'AYSO'],
];

// Photo-gallery cap by tier. Mirrors galleryCapForTier in
// functions/modules/clubSites.js (the server enforces it at publish).
function galleryCapForTier(tier) {
  switch (tier) {
    case 'club':          return 6;
    case 'growth':
    case 'founding_club': return 12;
    case 'elite':
    case 'enterprise':    return 30;
    default:              return 0;
  }
}

// Downscale + re-encode an image File client-side before upload (D-5):
// caps the longest edge, shrinks file size, and strips EXIF (incl. GPS).
function downscaleImage(file, maxEdge = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/webp', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('not a valid image')); };
    img.src = url;
  });
}

function defaultDraft(club) {
  return {
    brand: {
      color: club?.primaryColor || club?.branding?.primaryColor || '#1e3a8a',
      logoUrl: club?.logoUrl || club?.branding?.logoUrl || '',
    },
    hero: {
      headline: { en: club?.name || '', es: '' },
      sub: { en: '', es: '' },
    },
    announcement: { active: false, text: { en: '', es: '' } },
    contact: { email: club?.email || '', phone: club?.phone || '' },
    pages: [{ key: 'home', type: 'home', order: 0, visible: true, title: { en: 'Home', es: 'Inicio' } }],
    sponsors: [],
    publicTeamIds: [],
    sections: SECTION_ORDER_DEFAULT.map((key) => ({ key, visible: true })),
    heroImageUrl: '',
    gallery: [],
    affiliationLogos: {},
  };
}

/* ── Bilingual text field with an AI-translate helper (W-4 / D-7) ───────── */
function BilingualField({ label, value, onChange, onTranslate, multiline, max }) {
  const v = value || { en: '', es: '' };
  const [busy, setBusy] = useState(null); // 'en' | 'es' | null
  const Field = multiline ? 'textarea' : 'input';

  const translateInto = async (target) => {
    const source = target === 'es' ? v.en : v.es;
    if (!source?.trim()) { toast('Type the other language first'); return; }
    setBusy(target);
    try {
      const text = await onTranslate(source, target);
      onChange({ ...v, [target]: text });
    } catch {
      toast.error('Translation failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{label}</label>
      <div className="grid sm:grid-cols-2 gap-2">
        {['en', 'es'].map((lang) => (
          <div key={lang} className="relative">
            <span className="absolute left-2 top-2 text-[10px] font-bold uppercase text-zinc-400">{lang}</span>
            <Field
              className={clsx(inputCls, 'pt-6', multiline && 'min-h-[80px]')}
              value={v[lang] || ''}
              maxLength={max || 4000}
              onChange={(e) => onChange({ ...v, [lang]: e.target.value })}
            />
            <button
              type="button"
              onClick={() => translateInto(lang)}
              disabled={busy === lang}
              className="absolute right-1.5 bottom-1.5 inline-flex items-center gap-1 rounded-lg bg-[#b5f03d]/15 px-2 py-1 text-[11px] font-bold text-[#5a8a1f] hover:bg-[#b5f03d]/30 disabled:opacity-50"
              title={`Translate into ${lang.toUpperCase()} with Scrimly`}
            >
              <SparklesIcon className="h-3 w-3" />
              {busy === lang ? '…' : lang.toUpperCase()}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function ClubWebsiteEditor() {
  const { clubId } = useParams();
  const { club, isAdmin, isOwner, loading } = useClub(clubId);
  const fns = getFunctions();

  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewLang, setPreviewLang] = useState('en');
  const [uploading, setUploading] = useState(false);
  const [consent, setConsent] = useState(false);

  // Load draft + published meta + club teams once the club resolves.
  useEffect(() => {
    if (!club) return;
    let alive = true;
    (async () => {
      setLoadingDraft(true);
      try {
        const [draftSnap, pubSnap, teamSnap] = await Promise.all([
          getDoc(doc(db, 'clubSiteDrafts', clubId)),
          getDoc(doc(db, 'clubSites', clubId)),
          getDocs(query(collection(db, 'teams'), where('clubId', '==', clubId))),
        ]);
        if (!alive) return;
        setDraft(draftSnap.exists() ? draftSnap.data() : defaultDraft(club));
        setPublished(pubSnap.exists() ? pubSnap.data() : null);
        setTeams(teamSnap.docs.map((d) => ({ id: d.id, name: d.data().name || d.id })));
      } catch (e) {
        console.error('[ClubWebsiteEditor] load failed', e);
        toast.error('Could not load your site');
      } finally {
        if (alive) setLoadingDraft(false);
      }
    })();
    return () => { alive = false; };
  }, [club, clubId]);

  const patch = useCallback((updater) => {
    setDraft((d) => (d ? updater({ ...d }) : d));
  }, []);

  const saveDraft = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'clubSiteDrafts', clubId), { ...draft, updatedAt: serverTimestamp() });
      toast.success('Draft saved');
    } catch (e) {
      console.error('[ClubWebsiteEditor] save failed', e);
      toast.error('Could not save draft');
    } finally {
      setSaving(false);
    }
  };

  // Upload an image File: downscale → Storage → return the download URL.
  // opts.maxEdge/quality tune the re-encode; logos use a much smaller preset
  // than photos since they render tiny — keeps stored files to a few KB.
  const uploadImage = async (file, opts = {}) => {
    if (!file) return null;
    if (!/^image\//.test(file.type)) { toast.error('Please choose an image file.'); return null; }
    setUploading(true);
    try {
      const blob = await downscaleImage(file, opts.maxEdge ?? 1600, opts.quality ?? 0.82);
      const dir = opts.dir || 'media';
      const path = `clubSites/${clubId}/${dir}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.webp`;
      const { downloadURL } = await uploadBlobToStorage(blob, path);
      return downloadURL;
    } catch (e) {
      console.error('[ClubWebsiteEditor] image upload failed', e);
      toast.error('Image upload failed — try a different photo.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handlePublish = async () => {
    const needsC = !!draft.heroImageUrl || (draft.gallery || []).length > 0
      || (draft.staff || []).some((s) => s.photoUrl) || (draft.commits || []).length > 0
      || Object.keys(draft.affiliationLogos || {}).length > 0;
    if (needsC && !consent) {
      toast.error('Please confirm you have permission to publish the photos, names, and logos shown.');
      return;
    }
    setPublishing(true);
    try {
      await setDoc(doc(db, 'clubSiteDrafts', clubId), { ...draft, updatedAt: serverTimestamp() });
      // Compliance attestation (features/club-site-affiliation-logos). termsVersion
      // 'v1' is placeholder wording; lawyer-approved copy can be swapped later.
      const attestation = needsC ? {
        acknowledged: true,
        termsVersion: 'v1',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        appVersion: import.meta.env?.VITE_APP_VERSION || '',
      } : undefined;
      const res = await httpsCallable(fns, 'publishClubSite')({ clubId, attestation });
      const pubSnap = await getDoc(doc(db, 'clubSites', clubId));
      setPublished(pubSnap.exists() ? pubSnap.data() : null);
      toast.success(`Published · v${res.data?.version ?? ''}`);
    } catch (e) {
      console.error('[ClubWebsiteEditor] publish failed', e);
      toast.error(e?.message || 'Publish failed — your live site is unchanged');
    } finally {
      setPublishing(false);
    }
  };

  const translate = useCallback(async (text, targetLang) => {
    const res = await httpsCallable(fns, 'translateSiteField')({ clubId, text, targetLang });
    return res.data?.text || '';
  }, [fns, clubId]);

  // Pre-fill the coaching-staff list from the app's team members (owners/staff
  // of the selected public teams). Names/photos are denormalized on the member
  // docs; the director still edits titles/bios and confirms consent at publish.
  const STAFF_ROLES = ['owner', 'staff', 'head_coach', 'coach', 'manager', 'assistant', 'team_manager'];
  const importCoaches = async () => {
    const teamIds = draft.publicTeamIds || [];
    if (!teamIds.length) { toast('Select your public teams first.'); return; }
    try {
      const existing = new Set((draft.staff || []).map((s) => (s.name || '').trim().toLowerCase()));
      const found = new Map();
      for (const teamId of teamIds) {
        const snap = await getDocs(collection(db, 'teams', teamId, 'members'));
        snap.forEach((m) => {
          const d = m.data() || {};
          if (d.status && d.status !== 'active') return;
          const role = String(d.role || '').toLowerCase();
          if (!STAFF_ROLES.includes(role)) return;
          const name = String(d.displayName || '').trim();
          if (!name) return;
          const key = name.toLowerCase();
          if (existing.has(key) || found.has(key)) return;
          const title = (role === 'owner' || role === 'head_coach') ? 'Head Coach' : 'Coach';
          const photoUrl = typeof d.photoURL === 'string' && /^https:/i.test(d.photoURL) ? d.photoURL : '';
          found.set(key, { name, title, license: '', bio: '', photoUrl });
        });
      }
      const additions = [...found.values()];
      if (!additions.length) { toast('No new coaches found on your public teams.'); return; }
      patch((d) => { d.staff = [...(d.staff || []), ...additions]; return d; });
      toast.success(`Imported ${additions.length} coach${additions.length > 1 ? 'es' : ''} — review titles/photos, then publish.`);
    } catch (e) {
      console.error('[ClubWebsiteEditor] coach import failed', e);
      toast.error('Could not import coaches.');
    }
  };

  if (loading || loadingDraft) return <PageSpinner />;
  if (!club) return <div className="p-6 text-sm text-zinc-600">Club not found.</div>;
  if (!isAdmin && !isOwner) {
    return <div className="p-6 text-sm text-zinc-600">You need club admin access to edit the website.</div>;
  }
  if (!draft) return <PageSpinner />;

  const provisioned = !!club.siteSlug && club.siteEnabled !== false;
  const addablePages = ADDABLE_PAGES.filter((p) => !draft.pages.some((dp) => dp.type === p.type));
  const galleryCap = galleryCapForTier(club.tier);
  const gallery = draft.gallery || [];
  const hasImages = !!draft.heroImageUrl || gallery.length > 0;
  // Publishing people's names/photos (staff photos, college commits) also needs consent.
  const hasPeople = (draft.staff || []).some((s) => s.photoUrl) || (draft.commits || []).length > 0;
  // Affiliation logos are trademarks — publishing them requires an attestation too.
  const hasLogos = Object.keys(draft.affiliationLogos || {}).length > 0;
  const needsConsent = hasImages || hasPeople || hasLogos;
  const consentNeeded = needsConsent && !consent;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8 space-y-5">
      <Link to={`/clubs/${clubId}`} className="inline-flex items-center gap-1 text-sm text-[#5a8a1f] hover:underline">
        <ChevronLeftIcon className="h-4 w-4" /> Back to club
      </Link>

      {/* Header / status */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center">
              <GlobeAltIcon className="h-5 w-5 text-[#5a8a1f]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Club Website</h1>
              {provisioned ? (
                <a
                  href={`https://${club.siteSlug}.getscrimly.com`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#5a8a1f] hover:underline"
                >
                  {club.siteSlug}.getscrimly.com <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                </a>
              ) : (
                <p className="text-xs text-amber-700">Not set up yet — ask Scrimly to enable your site.</p>
              )}
              {published?.publishedAt && (
                <p className="text-[11px] text-zinc-400">Last published v{published.version}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveDraft} disabled={saving}
              className="rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save draft'}
            </button>
            <button onClick={handlePublish} disabled={publishing || !provisioned || consentNeeded}
              title={!provisioned ? 'Site must be enabled by Scrimly first' : (consentNeeded ? 'Confirm photo consent below first' : '')}
              className="rounded-xl bg-[#b5f03d] px-4 py-2 text-sm font-bold text-[#0d1f14] hover:brightness-95 disabled:opacity-50">
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </div>
        {needsConsent && (
          <label className="mt-3 flex items-start gap-2 text-xs text-zinc-700 border-t border-zinc-100 pt-3">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
            <span>I have permission to publish the photos, the names (players/staff), and any league/affiliation logos shown on this site — including consent for any identifiable minors, and confirming our club is a current member authorized to display each league's logo. <span className="text-zinc-400">(Required before you can publish.)</span></span>
          </label>
        )}
      </div>

      {/* Preview toggle */}
      <div className="flex justify-end">
        <button onClick={() => setPreview((p) => !p)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5a8a1f] hover:underline">
          {preview ? <PencilSquareIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          {preview ? 'Back to editor' : 'Preview'}
        </button>
      </div>

      {preview ? (
        <PreviewPane draft={draft} teams={teams} lang={previewLang} setLang={setPreviewLang} />
      ) : (
        <>
          {/* Brand */}
          <Section title="Brand">
            <div className="flex items-center gap-3">
              <input type="color" aria-label="Brand color" value={/^#[0-9a-fA-F]{6}$/.test(draft.brand.color) ? draft.brand.color : '#1e3a8a'}
                onChange={(e) => patch((d) => { d.brand = { ...d.brand, color: e.target.value }; return d; })}
                className="h-10 w-12 rounded-md border border-zinc-300" />
              <input className={inputCls} value={draft.brand.color || ''} maxLength={7} placeholder="#1e3a8a"
                onChange={(e) => patch((d) => { d.brand = { ...d.brand, color: e.target.value }; return d; })} />
            </div>
            <input className={inputCls} value={draft.brand.logoUrl || ''} placeholder="Logo URL (https://…)"
              onChange={(e) => patch((d) => { d.brand = { ...d.brand, logoUrl: e.target.value }; return d; })} />
          </Section>

          {/* Hero */}
          <Section title="Hero">
            <BilingualField label="Headline" value={draft.hero.headline} max={160} onTranslate={translate}
              onChange={(val) => patch((d) => { d.hero = { ...d.hero, headline: val }; return d; })} />
            <BilingualField label="Subheadline" value={draft.hero.sub} max={240} onTranslate={translate}
              onChange={(val) => patch((d) => { d.hero = { ...d.hero, sub: val }; return d; })} />
            <div className="pt-1">
              <label className="block text-sm font-semibold text-[#0d1f14] mb-1">Hero photo</label>
              {draft.heroImageUrl ? (
                <div className="flex items-center gap-3">
                  <img src={draft.heroImageUrl} alt="" className="h-16 w-28 object-cover rounded-lg border border-zinc-200" />
                  <button type="button" onClick={() => patch((d) => { d.heroImageUrl = ''; return d; })}
                    className="inline-flex items-center gap-1 text-sm text-rose-600"><TrashIcon className="h-4 w-4" /> Remove</button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 rounded-xl border border-dashed border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-600 cursor-pointer hover:border-[#b5f03d]">
                  <PhotoIcon className="h-4 w-4" /> {uploading ? 'Uploading…' : 'Upload hero photo'}
                  <input type="file" accept="image/*" className="hidden" disabled={uploading}
                    onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; const url = await uploadImage(f); if (url) patch((d) => { d.heroImageUrl = url; return d; }); }} />
                </label>
              )}
              <p className="text-xs text-zinc-400 mt-1">Shown behind your hero with a dark overlay. A wide team/action photo works best.</p>
            </div>
          </Section>

          {/* Announcement */}
          <Section title="Announcement banner">
            <label className="flex items-center gap-2 text-sm text-[#0d1f14]">
              <input type="checkbox" checked={draft.announcement?.active === true}
                onChange={(e) => patch((d) => { d.announcement = { ...(d.announcement || {}), active: e.target.checked }; return d; })} />
              Show a banner ribbon under the hero
            </label>
            {draft.announcement?.active && (
              <BilingualField label="Banner text" value={draft.announcement?.text} max={400} onTranslate={translate}
                onChange={(val) => patch((d) => { d.announcement = { ...(d.announcement || {}), text: val }; return d; })} />
            )}
          </Section>

          {/* Public teams */}
          <Section title="Public teams">
            <p className="text-xs text-zinc-500">Only teams you check appear on the site (schedule + age groups). Private teams stay hidden.</p>
            {teams.length === 0 ? (
              <p className="text-sm text-zinc-400">No teams in this club yet.</p>
            ) : teams.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-sm text-[#0d1f14]">
                <input type="checkbox" checked={draft.publicTeamIds?.includes(t.id) || false}
                  onChange={(e) => patch((d) => {
                    const set = new Set(d.publicTeamIds || []);
                    e.target.checked ? set.add(t.id) : set.delete(t.id);
                    d.publicTeamIds = [...set];
                    return d;
                  })} />
                {t.name}
              </label>
            ))}
          </Section>

          {/* Sections — order & visibility */}
          <Section title="Sections — order &amp; visibility">
            <p className="text-xs text-zinc-500">Choose which sections appear on your public site and in what order. A section with no content stays hidden automatically.</p>
            {resolveSections(draft.sections).map((s, i, arr) => (
              <div key={s.key} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 px-3 py-2">
                <label className="flex items-center gap-2 text-sm text-[#0d1f14]">
                  <input type="checkbox" checked={s.visible !== false}
                    onChange={(e) => patch((d) => { d.sections = resolveSections(d.sections).map((x) => (x.key === s.key ? { ...x, visible: e.target.checked } : x)); return d; })} />
                  {SECTION_LABEL[s.key] || s.key}
                </label>
                <div className="flex items-center gap-1">
                  <button type="button" disabled={i === 0} title="Move up"
                    onClick={() => patch((d) => { d.sections = moveSection(resolveSections(d.sections), i, -1); return d; })}
                    className="rounded-md border border-zinc-200 p-1 text-zinc-500 hover:bg-zinc-50 disabled:opacity-30"><ChevronUpIcon className="h-4 w-4" /></button>
                  <button type="button" disabled={i === arr.length - 1} title="Move down"
                    onClick={() => patch((d) => { d.sections = moveSection(resolveSections(d.sections), i, 1); return d; })}
                    className="rounded-md border border-zinc-200 p-1 text-zinc-500 hover:bg-zinc-50 disabled:opacity-30"><ChevronDownIcon className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </Section>

          {/* Affiliations */}
          <Section title="Affiliations / leagues">
            <p className="text-xs text-zinc-500">Shown near the top of your site. Check the leagues you belong to, then optionally upload each league's logo.</p>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
              League logos are trademarks owned by each league. Upload only logos you are authorized to display as a current member — you'll confirm this by the Publish button.
            </div>
            <div className="space-y-2">
              {AFFILIATION_OPTIONS.map(([key, label]) => {
                const checked = draft.affiliations?.includes(key) || false;
                const logo = draft.affiliationLogos?.[key] || '';
                return (
                  <div key={key} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 px-3 py-2">
                    <label className="flex items-center gap-2 text-sm text-[#0d1f14]">
                      <input type="checkbox" checked={checked}
                        onChange={(e) => patch((d) => {
                          const set = new Set(d.affiliations || []);
                          if (e.target.checked) { set.add(key); }
                          else { set.delete(key); const m = { ...(d.affiliationLogos || {}) }; delete m[key]; d.affiliationLogos = m; }
                          d.affiliations = [...set];
                          return d;
                        })} />
                      {label}
                    </label>
                    {checked && (
                      logo ? (
                        <div className="flex items-center gap-2">
                          <img src={logo} alt="" className="h-8 w-auto max-w-[96px] object-contain" />
                          <button onClick={() => patch((d) => { const m = { ...(d.affiliationLogos || {}) }; delete m[key]; d.affiliationLogos = m; return d; })}
                            className="text-zinc-400 hover:text-rose-600"><TrashIcon className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <label className="inline-flex items-center gap-1 text-xs font-semibold text-[#5a8a1f] cursor-pointer">
                          <PhotoIcon className="h-4 w-4" /> Logo
                          <input type="file" accept="image/*" className="hidden" disabled={uploading}
                            onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; const url = await uploadImage(f, { maxEdge: 384, quality: 0.85, dir: 'affiliations' }); if (url) patch((d) => { d.affiliationLogos = { ...(d.affiliationLogos || {}), [key]: url }; return d; }); }} />
                        </label>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Text sections (About / custom) */}
          <Section title={`Text sections (${draft.pages.filter((p) => p.type !== 'home').length})`} right={
            addablePages.length > 0 && (
              <select className="rounded-lg border border-zinc-300 px-2 py-1 text-xs" value=""
                onChange={(e) => {
                  const pick = ADDABLE_PAGES.find((p) => p.type === e.target.value);
                  if (!pick) return;
                  patch((d) => {
                    d.pages = [...d.pages, {
                      key: pick.type, type: pick.type, order: d.pages.length, visible: true,
                      title: { en: pick.label, es: '' },
                      ...(pick.bilingualBody ? { body: { en: '', es: '' } } : {}),
                    }];
                    return d;
                  });
                }}>
                <option value="" disabled>+ Add section</option>
                {addablePages.map((p) => <option key={p.type} value={p.type}>{p.label}</option>)}
              </select>
            )
          }>
            <p className="text-xs text-zinc-500">Your site is one scrolling page. An About or custom text block becomes a <strong>section</strong> on that page (ordered in “Sections” above) — not a separate page. Teams, schedule, sponsors, and contact appear automatically.</p>
            {draft.pages.map((p, i) => (p.type === 'home' ? null : (
              <div key={p.key} className="rounded-xl border border-zinc-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold capitalize">{p.type === 'text' ? 'Custom text' : p.type}</span>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 text-xs text-zinc-500">
                      <input type="checkbox" checked={p.visible !== false}
                        onChange={(e) => patch((d) => { d.pages[i] = { ...d.pages[i], visible: e.target.checked }; return d; })} />
                      Visible
                    </label>
                    <button onClick={() => patch((d) => { d.pages = d.pages.filter((_, j) => j !== i); return d; })}
                      className="text-zinc-400 hover:text-rose-600"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                </div>
                <BilingualField label="Title" value={p.title} max={120} onTranslate={translate}
                  onChange={(val) => patch((d) => { d.pages[i] = { ...d.pages[i], title: val }; return d; })} />
                {(p.type === 'about' || p.type === 'text') && (
                  <BilingualField label="Body" value={p.body} multiline max={5000} onTranslate={translate}
                    onChange={(val) => patch((d) => { d.pages[i] = { ...d.pages[i], body: val }; return d; })} />
                )}
              </div>
            )))}
            {draft.pages.filter((p) => p.type !== 'home').length === 0 && (
              <p className="text-sm text-zinc-400">No text sections yet — add an About or custom text block.</p>
            )}
          </Section>

          {/* News */}
          <Section title="News" right={
            <button onClick={() => patch((d) => { d.news = [...(d.news || []), { title: { en: '', es: '' }, body: { en: '', es: '' }, date: new Date().toISOString().slice(0, 10) }]; return d; })}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a1f]"><PlusIcon className="h-3 w-3" /> Add</button>
          }>
            {(draft.news || []).map((n, i) => (
              <div key={i} className="rounded-xl border border-zinc-200 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <input type="date" className={clsx(inputCls, 'w-44')} value={n.date || ''} onChange={(e) => patch((d) => { d.news[i] = { ...d.news[i], date: e.target.value }; return d; })} />
                  <button onClick={() => patch((d) => { d.news = d.news.filter((_, j) => j !== i); return d; })} className="text-zinc-400 hover:text-rose-600"><TrashIcon className="h-4 w-4" /></button>
                </div>
                <BilingualField label="Title" value={n.title} max={160} onTranslate={translate} onChange={(val) => patch((d) => { d.news[i] = { ...d.news[i], title: val }; return d; })} />
                <BilingualField label="Body" value={n.body} multiline max={2000} onTranslate={translate} onChange={(val) => patch((d) => { d.news[i] = { ...d.news[i], body: val }; return d; })} />
              </div>
            ))}
          </Section>

          {/* Camps & clinics */}
          <Section title="Camps & clinics" right={
            <button onClick={() => patch((d) => { d.camps = [...(d.camps || []), { title: { en: '', es: '' }, body: { en: '', es: '' }, dates: '', url: '' }]; return d; })}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a1f]"><PlusIcon className="h-3 w-3" /> Add</button>
          }>
            {(draft.camps || []).map((c, i) => (
              <div key={i} className="rounded-xl border border-zinc-200 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <input className={inputCls} placeholder="Dates (e.g. Jul 7–11)" value={c.dates || ''} onChange={(e) => patch((d) => { d.camps[i] = { ...d.camps[i], dates: e.target.value }; return d; })} />
                  <button onClick={() => patch((d) => { d.camps = d.camps.filter((_, j) => j !== i); return d; })} className="text-zinc-400 hover:text-rose-600"><TrashIcon className="h-4 w-4" /></button>
                </div>
                <BilingualField label="Title" value={c.title} max={160} onTranslate={translate} onChange={(val) => patch((d) => { d.camps[i] = { ...d.camps[i], title: val }; return d; })} />
                <BilingualField label="Details" value={c.body} multiline max={1200} onTranslate={translate} onChange={(val) => patch((d) => { d.camps[i] = { ...d.camps[i], body: val }; return d; })} />
                <input className={inputCls} placeholder="Register/info link (https://…)" value={c.url || ''} onChange={(e) => patch((d) => { d.camps[i] = { ...d.camps[i], url: e.target.value }; return d; })} />
              </div>
            ))}
          </Section>

          {/* Photos */}
          <Section title={`Photos (${gallery.length}${galleryCap ? `/${galleryCap}` : ''})`}>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
              Only upload photos your club has permission to publish — these appear on a public website. Avoid identifying minors without media-release consent from their families.
            </div>
            <div className="flex flex-wrap gap-3">
              {gallery.map((g, i) => (
                <div key={i} className="relative">
                  <img src={g.url} alt="" className="h-20 w-28 object-cover rounded-lg border border-zinc-200" />
                  <button type="button" onClick={() => patch((d) => { d.gallery = (d.gallery || []).filter((_, j) => j !== i); return d; })}
                    className="absolute -top-2 -right-2 bg-white rounded-full border border-zinc-200 p-0.5 text-rose-600"><TrashIcon className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {gallery.length < galleryCap ? (
                <label className="h-20 w-28 grid place-items-center rounded-lg border border-dashed border-zinc-300 cursor-pointer hover:border-[#b5f03d] text-zinc-500">
                  {uploading ? '…' : <PlusIcon className="h-5 w-5" />}
                  <input type="file" accept="image/*" className="hidden" disabled={uploading}
                    onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; const url = await uploadImage(f); if (url) patch((d) => { d.gallery = [...(d.gallery || []), { url }]; return d; }); }} />
                </label>
              ) : (
                <div className="h-20 w-28 grid place-items-center rounded-lg border border-zinc-200 text-[11px] text-center text-zinc-400 px-2">
                  {galleryCap === 0 ? 'Upgrade your plan to add photos' : 'Plan photo limit reached'}
                </div>
              )}
            </div>
          </Section>

          {/* College commitments */}
          <Section title="College commitments" right={
            <button onClick={() => patch((d) => { d.commits = [...(d.commits || []), { playerName: '', college: '', gradYear: '', photoUrl: '' }]; return d; })}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a1f]"><PlusIcon className="h-3 w-3" /> Add</button>
          }>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
              Publishing player names on a public site — confirm you have family consent (the consent box by Publish covers this).
            </div>
            {(draft.commits || []).map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                {c.photoUrl
                  ? <div className="relative flex-shrink-0">
                      <img src={c.photoUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
                      <button type="button" onClick={() => patch((d) => { d.commits[i] = { ...d.commits[i], photoUrl: '' }; return d; })}
                        className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-zinc-200 p-0.5 text-rose-600"><TrashIcon className="h-3 w-3" /></button>
                    </div>
                  : <label className="h-12 w-12 grid place-items-center rounded-full border border-dashed border-zinc-300 cursor-pointer text-zinc-400 flex-shrink-0">
                      <PhotoIcon className="h-4 w-4" />
                      <input type="file" accept="image/*" className="hidden" disabled={uploading}
                        onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; const url = await uploadImage(f, { maxEdge: 512, quality: 0.82, dir: 'people' }); if (url) patch((d) => { d.commits[i] = { ...d.commits[i], photoUrl: url }; return d; }); }} />
                    </label>}
                <div className="grid sm:grid-cols-3 gap-2 flex-1 min-w-0">
                  <input className={inputCls} placeholder="Player name" value={c.playerName || ''} onChange={(e) => patch((d) => { d.commits[i] = { ...d.commits[i], playerName: e.target.value }; return d; })} />
                  <input className={inputCls} placeholder="College" value={c.college || ''} onChange={(e) => patch((d) => { d.commits[i] = { ...d.commits[i], college: e.target.value }; return d; })} />
                  <input className={inputCls} placeholder="Grad year" value={c.gradYear || ''} onChange={(e) => patch((d) => { d.commits[i] = { ...d.commits[i], gradYear: e.target.value }; return d; })} />
                </div>
                <button onClick={() => patch((d) => { d.commits = d.commits.filter((_, j) => j !== i); return d; })} className="text-zinc-400 hover:text-rose-600 flex-shrink-0"><TrashIcon className="h-4 w-4" /></button>
              </div>
            ))}
          </Section>

          {/* Coaching staff */}
          <Section title="Coaching staff" right={
            <div className="flex items-center gap-3">
              <button onClick={importCoaches}
                className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a1f]"><ArrowDownTrayIcon className="h-3.5 w-3.5" /> Import from app</button>
              <button onClick={() => patch((d) => { d.staff = [...(d.staff || []), { name: '', title: '', license: '', bio: '', photoUrl: '' }]; return d; })}
                className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a1f]"><PlusIcon className="h-3 w-3" /> Add</button>
            </div>
          }>
            <p className="text-xs text-zinc-500">Adults representing the club — make sure you have their permission to publish names/photos. "Import from app" pulls coaches assigned to your public teams.</p>
            {(draft.staff || []).map((s, i) => (
              <div key={i} className="rounded-xl border border-zinc-200 p-3 space-y-2">
                <div className="flex items-center gap-3">
                  {s.photoUrl
                    ? <img src={s.photoUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
                    : <label className="h-12 w-12 grid place-items-center rounded-full border border-dashed border-zinc-300 cursor-pointer text-zinc-400 flex-shrink-0">
                        <PhotoIcon className="h-4 w-4" />
                        <input type="file" accept="image/*" className="hidden" disabled={uploading}
                          onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; const url = await uploadImage(f); if (url) patch((d) => { d.staff[i] = { ...d.staff[i], photoUrl: url }; return d; }); }} />
                      </label>}
                  <input className={inputCls} placeholder="Name" value={s.name || ''} onChange={(e) => patch((d) => { d.staff[i] = { ...d.staff[i], name: e.target.value }; return d; })} />
                  <button onClick={() => patch((d) => { d.staff = d.staff.filter((_, j) => j !== i); return d; })} className="text-zinc-400 hover:text-rose-600"><TrashIcon className="h-4 w-4" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className={inputCls} placeholder="Title (e.g. Director of Coaching)" value={s.title || ''} onChange={(e) => patch((d) => { d.staff[i] = { ...d.staff[i], title: e.target.value }; return d; })} />
                  <input className={inputCls} placeholder="License (e.g. USSF B)" value={s.license || ''} onChange={(e) => patch((d) => { d.staff[i] = { ...d.staff[i], license: e.target.value }; return d; })} />
                </div>
                <input className={inputCls} placeholder="Short bio (optional)" value={s.bio || ''} onChange={(e) => patch((d) => { d.staff[i] = { ...d.staff[i], bio: e.target.value }; return d; })} />
              </div>
            ))}
          </Section>

          {/* Sponsors */}
          <Section title="Sponsors" right={
            <button onClick={() => patch((d) => { d.sponsors = [...(d.sponsors || []), { name: '', logoUrl: '', url: '' }]; return d; })}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a1f]"><PlusIcon className="h-3 w-3" /> Add</button>
          }>
            {(draft.sponsors || []).length === 0 && <p className="text-sm text-zinc-400">No sponsors yet.</p>}
            {(draft.sponsors || []).map((s, i) => (
              <div key={i} className="grid sm:grid-cols-3 gap-2 items-center">
                <input className={inputCls} placeholder="Name" value={s.name || ''}
                  onChange={(e) => patch((d) => { d.sponsors[i] = { ...d.sponsors[i], name: e.target.value }; return d; })} />
                <input className={inputCls} placeholder="Logo URL (https://…)" value={s.logoUrl || ''}
                  onChange={(e) => patch((d) => { d.sponsors[i] = { ...d.sponsors[i], logoUrl: e.target.value }; return d; })} />
                <div className="flex gap-2">
                  <input className={inputCls} placeholder="Link (https://…)" value={s.url || ''}
                    onChange={(e) => patch((d) => { d.sponsors[i] = { ...d.sponsors[i], url: e.target.value }; return d; })} />
                  <button onClick={() => patch((d) => { d.sponsors = d.sponsors.filter((_, j) => j !== i); return d; })}
                    className="text-zinc-400 hover:text-rose-600"><TrashIcon className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </Section>

          {/* Social links */}
          <Section title="Social links">
            <p className="text-xs text-zinc-500">Shown as icons in your site footer. Leave blank to hide. (https only)</p>
            {SOCIAL_PLATFORMS.map(({ key, label, placeholder }) => (
              <input key={key} className={inputCls} placeholder={`${label} — ${placeholder}`} value={draft.social?.[key] || ''}
                onChange={(e) => patch((d) => { d.social = { ...(d.social || {}), [key]: e.target.value }; return d; })} />
            ))}
          </Section>

          {/* Contact */}
          <Section title="Contact">
            <input className={inputCls} placeholder="Public email" value={draft.contact.email || ''}
              onChange={(e) => patch((d) => { d.contact = { ...d.contact, email: e.target.value }; return d; })} />
            <input className={inputCls} placeholder="Public phone" value={draft.contact.phone || ''}
              onChange={(e) => patch((d) => { d.contact = { ...d.contact, phone: e.target.value }; return d; })} />
          </Section>
        </>
      )}
    </div>
  );
}

/* ── In-app preview (renders the draft; the real public render is the same  */
/*    shape served by the standalone renderer at flip-time — A-10) ───────── */
function PreviewPane({ draft, teams, lang, setLang }) {
  const tx = (b) => (b && (b[lang] || b.en)) || '';
  const publicTeams = teams.filter((t) => draft.publicTeamIds?.includes(t.id));
  const color = /^#[0-9a-fA-F]{6}$/.test(draft.brand.color) ? draft.brand.color : '#1e3a8a';
  const ordered = resolveSections(draft.sections);
  const affs = (draft.affiliations || []).filter((k) => AFFILIATION_OPTIONS.some(([key]) => key === k));
  const pages = draft.pages.filter((p) => p.visible !== false && (p.type === 'about' || p.type === 'text'));
  const news = (draft.news || []).filter((n) => tx(n.title));
  const camps = (draft.camps || []).filter((c) => tx(c.title));
  const photos = (draft.gallery || []).filter((g) => g.url);
  const commits = (draft.commits || []).filter((c) => c.playerName);
  const staff = (draft.staff || []).filter((s) => s.name);
  const sponsors = (draft.sponsors || []).filter((s) => s.logoUrl);
  const note = (txt) => <p className="text-xs text-zinc-400 italic">{txt}</p>;
  const previewEls = {
    affiliations: affs.length > 0 && (
      <div className="flex flex-wrap items-center gap-2">
        {affs.map((k) => {
          const label = AFFILIATION_OPTIONS.find(([key]) => key === k)?.[1] || k;
          const logo = draft.affiliationLogos?.[k];
          return logo
            ? <img key={k} src={logo} alt={label} title={label} className="h-8 w-auto max-w-[96px] object-contain" />
            : <span key={k} className="rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide" style={{ color, borderColor: color }}>{label}</span>;
        })}
      </div>
    ),
    about: pages.length > 0 && (
      <>{pages.map((p) => (
        <div key={p.key} className="mb-3 last:mb-0">
          <h3 className="font-bold text-[#0d1f14]">{tx(p.title)}</h3>
          <p className="text-sm text-zinc-600 whitespace-pre-wrap">{tx(p.body)}</p>
        </div>
      ))}</>
    ),
    teams: publicTeams.length > 0 && (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Equipos' : 'Teams'}</h3>
        <p className="text-sm text-zinc-600">{publicTeams.map((t) => t.name).join(' · ')}</p>
      </div>
    ),
    seasons: (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Inscripciones' : 'Registration'}</h3>
        {note(lang === 'es' ? 'Las temporadas abiertas (precio, fechas, qué incluye) aparecen automáticamente al publicar.' : 'Open seasons (price, dates, what\'s included) appear automatically when you publish.')}
      </div>
    ),
    schedule: (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Próximos partidos' : 'Upcoming games'}</h3>
        {note(lang === 'es' ? 'Se generan automáticamente de tus equipos públicos al publicar.' : 'Auto-generated from your public teams when you publish.')}
      </div>
    ),
    results: (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Resultados recientes y récord' : 'Recent results & record'}</h3>
        {note(lang === 'es' ? 'Se generan automáticamente de los marcadores finales al publicar.' : 'Auto-generated from final scores when you publish.')}
      </div>
    ),
    news: news.length > 0 && (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Noticias' : 'News'}</h3>
        <div className="space-y-2 mt-1">
          {news.map((n, i) => (
            <div key={i} className="rounded-lg border border-zinc-200 p-2">
              {n.date && <div className="text-[11px] font-bold uppercase" style={{ color }}>{n.date}</div>}
              <div className="text-sm font-semibold text-[#0d1f14]">{tx(n.title)}</div>
              {tx(n.body) && <p className="text-xs text-zinc-600 whitespace-pre-wrap">{tx(n.body)}</p>}
            </div>
          ))}
        </div>
      </div>
    ),
    camps: camps.length > 0 && (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Campamentos y clínicas' : 'Camps & clinics'}</h3>
        <div className="grid sm:grid-cols-2 gap-2 mt-1">
          {camps.map((c, i) => (
            <div key={i} className="rounded-lg border border-zinc-200 p-2">
              <div className="text-sm font-semibold text-[#0d1f14]">{tx(c.title)}</div>
              {c.dates && <div className="text-xs font-bold" style={{ color }}>{c.dates}</div>}
              {tx(c.body) && <p className="text-xs text-zinc-600">{tx(c.body)}</p>}
            </div>
          ))}
        </div>
      </div>
    ),
    gallery: photos.length > 0 && (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Fotos' : 'Photos'}</h3>
        <div className="flex flex-wrap gap-2 mt-1">
          {photos.map((g, i) => <img key={i} src={g.url} alt="" className="h-16 w-20 object-cover rounded-md" />)}
        </div>
      </div>
    ),
    commits: commits.length > 0 && (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Compromisos universitarios' : 'College commitments'}</h3>
        <div className="space-y-1 mt-1">
          {commits.map((c, i) => (
            <p key={i} className="text-sm text-zinc-600"><span className="font-semibold text-[#0d1f14]">{c.playerName}</span>{c.college ? ` · ${c.college}` : ''}{c.gradYear ? ` (${c.gradYear})` : ''}</p>
          ))}
        </div>
      </div>
    ),
    staff: staff.length > 0 && (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Cuerpo técnico' : 'Coaching staff'}</h3>
        <div className="flex flex-wrap gap-3 mt-1">
          {staff.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              {s.photoUrl && <img src={s.photoUrl} alt="" className="h-8 w-8 rounded-full object-cover" />}
              <div className="text-xs"><div className="font-semibold text-[#0d1f14]">{s.name}</div>{s.title && <div className="text-zinc-500">{s.title}</div>}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    sponsors: sponsors.length > 0 && (
      <div>
        <h3 className="font-bold text-[#0d1f14]">{lang === 'es' ? 'Patrocinadores' : 'Sponsors'}</h3>
        <div className="flex flex-wrap gap-3 mt-1">
          {sponsors.map((s, i) => <img key={i} src={s.logoUrl} alt={s.name} className="h-10 object-contain" />)}
        </div>
      </div>
    ),
  };
  return (
    <div className="rounded-2xl border border-zinc-200 overflow-hidden">
      <div className="flex justify-end gap-1 bg-zinc-100 p-2">
        {['en', 'es'].map((l) => (
          <button key={l} onClick={() => setLang(l)}
            className={clsx('rounded-md px-2 py-1 text-xs font-bold', lang === l ? 'bg-[#b5f03d] text-[#0d1f14]' : 'text-zinc-500')}>
            {l === 'en' ? '🇺🇸 EN' : '🇲🇽 ES'}
          </button>
        ))}
      </div>
      <div className="p-6 text-white" style={draft.heroImageUrl
        ? { backgroundImage: `linear-gradient(rgba(13,31,20,.55),rgba(13,31,20,.72)), url(${draft.heroImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { backgroundColor: color }}>
        {draft.brand.logoUrl && <img src={draft.brand.logoUrl} alt="" className="h-12 mb-3 object-contain" />}
        <h2 className="text-2xl font-extrabold" style={{ fontFamily: HEAD_FONT }}>{tx(draft.hero.headline) || '—'}</h2>
        <p className="opacity-90">{tx(draft.hero.sub)}</p>
      </div>
      {draft.announcement?.active && tx(draft.announcement.text) && (
        <div className="px-6 py-2.5 text-center text-sm font-semibold text-white" style={{ background: color }}>{tx(draft.announcement.text)}</div>
      )}
      <div className="p-6 space-y-5 bg-white">
        {ordered.map((s) => (s.visible !== false && previewEls[s.key]
          ? <div key={s.key}>{previewEls[s.key]}</div>
          : null))}
        {(draft.contact.email || draft.contact.phone) && (
          <p className="text-xs text-zinc-500">{[draft.contact.email, draft.contact.phone].filter(Boolean).join(' · ')}</p>
        )}
        {Object.entries(draft.social || {}).filter(([, u]) => u).length > 0 && (
          <p className="text-xs text-zinc-500">{Object.entries(draft.social).filter(([, u]) => u).map(([k]) => SOCIAL_PLATFORMS.find((p) => p.key === k)?.label || k).join(' · ')}</p>
        )}
        <p className="text-[10px] text-zinc-300 pt-4 border-t">Powered by Scrimly</p>
      </div>
    </div>
  );
}
